import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SkeletonTestimonial } from '../components/Skeleton';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';

const BRAND_BLUE = '#1B3A8C';
const SHOW_TESTIMONIALS = true;
const BRAND_RED = '#D41F3A';

// SVG product placeholders
const PinSVG = ({ color = BRAND_BLUE, size = 120 }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
    <circle cx="60" cy="60" r="52" fill={color} opacity="0.12" stroke={color} strokeWidth="2"/>
    <circle cx="60" cy="60" r="38" fill={color} opacity="0.18"/>
    <circle cx="60" cy="60" r="24" fill={color} opacity="0.9"/>
    <circle cx="60" cy="60" r="12" fill="white" opacity="0.9"/>
    <circle cx="60" cy="60" r="5" fill={color}/>
    <line x1="60" y1="108" x2="60" y2="120" stroke={color} strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const MedalSVG = ({ size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="48" r="28" fill="#D97706" opacity="0.15" stroke="#D97706" strokeWidth="2"/>
    <circle cx="40" cy="48" r="20" fill="#D97706" opacity="0.8"/>
    <circle cx="40" cy="48" r="12" fill="white" opacity="0.9"/>
    <rect x="35" y="8" width="10" height="24" rx="3" fill="#D97706" opacity="0.7"/>
    <rect x="28" y="4" width="24" height="10" rx="3" fill="#D97706"/>
  </svg>
);

const CoinSVG = ({ size = 70 }) => (
  <svg width={size} height={size} viewBox="0 0 70 70" fill="none">
    <circle cx="35" cy="35" r="32" fill="#92400E" opacity="0.1" stroke="#92400E" strokeWidth="2"/>
    <circle cx="35" cy="35" r="24" fill="#92400E" opacity="0.7"/>
    <circle cx="35" cy="35" r="16" fill="#92400E" opacity="0.9"/>
    <text x="35" y="40" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">$</text>
  </svg>
);

const PatchSVG = ({ size = 70 }) => (
  <svg width={size} height={size} viewBox="0 0 70 70" fill="none">
    <rect x="8" y="12" width="54" height="46" rx="8" fill="#DC2626" opacity="0.15" stroke="#DC2626" strokeWidth="2"/>
    <rect x="14" y="18" width="42" height="34" rx="6" fill="#DC2626" opacity="0.8"/>
    <circle cx="35" cy="35" r="10" fill="white" opacity="0.9"/>
  </svg>
);

const PRODUCTS = [
  { name: 'Lapel Pins', slug: 'lapel-pins', color: BRAND_BLUE, desc: 'Soft enamel, hard enamel, die struck and printed — for brands, events and collectors.', svg: <PinSVG color={BRAND_BLUE} size={160}/> },
  { name: 'Trading Pins', slug: 'trading-pins', color: '#DC2626', desc: 'Custom baseball trading pins with spinners, danglers and creative die-cut shapes.', svg: <PinSVG color="#DC2626" size={160}/> },
  { name: 'Medals', slug: 'medals', color: '#D97706', desc: 'Sports, academic and corporate medals for every achievement and ceremony.', svg: <MedalSVG size={160}/> },
  { name: 'Challenge Coins', slug: 'coins', color: '#92400E', desc: 'Military, law enforcement and corporate challenge coins with antique finishes.', svg: <CoinSVG size={160}/> },
  { name: 'PVC Patches', slug: 'pvc-patches', color: '#DC2626', desc: 'Flexible rubber patches — waterproof, bold and durable for any surface.', svg: <PatchSVG size={160}/> },
  { name: 'Keychains', slug: 'keychains', color: '#065F46', desc: 'Custom metal keychains in any shape — promotional giveaways and retail merchandise.', svg: <PinSVG color="#065F46" size={160}/> },
  { name: 'Charms', slug: 'charms', color: '#7C3AED', desc: 'Miniature custom charms for bracelets, necklaces and zipper pulls.', svg: <PinSVG color="#7C3AED" size={160}/> },
];

const PIN_STYLES = [
  { name: 'Soft Enamel', image: '/images/homePage_customPins_softEnamel.webp', desc: 'Colors in recessed areas, raised metal outlines. Classic textured feel. The most popular choice for trading pins, brand merchandise and events.' },
  { name: 'Hard Enamel', image: '/images/homePage_customPins_hardEnamel.webp', desc: 'Smooth polished surface, jewelry-quality finish. Perfect for corporate logos, employee recognition and premium merchandise.' },
  { name: 'Printed', image: '/images/homePage_customPins_printed.webp', desc: 'Full color, gradients and complex artwork without metal line restrictions. Ideal for detailed logos and photographic designs.' },
  { name: 'Die Struck', image: '/images/homePage_customPins_diestruck.webp', desc: 'Metal only, no enamel. Timeless corporate and military style with raised and recessed metal areas.' },
  { name: '3D Sculptured', image: '/images/homePage_customPins_3Dsculpted.webp', desc: 'Multi-level dimensional design. Ideal for mascots, faces, animals and buildings that need real depth.' },
];

const STATS = [
  { value: '1M+', label: 'Pins & Products Delivered' },
  { value: '10K+', label: 'Happy Customers' },
  { value: '120', label: 'Min Proof Turnaround' },
  { value: '15+', label: 'Years in Business' },
];

// Animated section wrapper
const EASE_OUT = [0.16, 1, 0.3, 1];

// Section entrance — 24px upward, 600ms ease-out (spec: 20-30px)
// Background video. The poster image paints first; the 3 MB video starts only after the
// page has loaded and gone quiet, so it never competes with the scripts and the LCP image.
// Skipped for visitors who ask for reduced motion or Data Saver.
const HeroVideo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduce || saveData) return undefined;
    let timer;
    const start = () => {
      timer = setTimeout(() => {
        v.preload = 'auto';
        const playing = v.play();
        if (playing && playing.catch) playing.catch(() => {});
      }, 1200);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => { clearTimeout(timer); window.removeEventListener('load', start); };
  }, []);
  return (
    <video
      ref={ref} muted loop playsInline preload="none"
      poster="/images/lapelpinPage_heroSection_lapelPin.webp"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, opacity: 0.45 }}>
      <source src="/images/pinManufacturing_heroSection.mp4" type="video/mp4" />
    </video>
  );
};

const FadeUp = ({ children, delay = 0, y = 36 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: EASE_OUT }}>
      {children}
    </motion.div>
  );
};

// Subtle slide-in — reduced from 80px to 30px (less dramatic)
const SlideIn = ({ children, direction = 'left', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const x = direction === 'left' ? -50 : 50;
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, x }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: EASE_OUT }}>
      {children}
    </motion.div>
  );
};

// Staggered children container
const StaggerContainer = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.08, delayChildren: delay } }
      }}>
      {children}
    </motion.div>
  );
};

// Individual stagger child
const StaggerChild = ({ children, y = 30 }) => (
  <motion.div variants={{
    hidden: { opacity: 0, y },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE_OUT } }
  }}>
    {children}
  </motion.div>
);

// Product carousel section
const ProductCarousel = () => {
  const [active, setActive] = useState(0);
  const product = PRODUCTS[active];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)' }}>
        <div style={{ display: 'flex', flexDirection: window.innerWidth <= 768 ? 'column-reverse' : 'row', gap: window.innerWidth <= 768 ? 32 : 80, alignItems: 'center', paddingTop: window.innerWidth <= 768 ? 32 : 0 }}>
          {/* Left — product visual */}
          <SlideIn direction="left">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: -30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                width: 320, height: 320, borderRadius: '50%',
                background: `radial-gradient(circle at 40% 40%, ${product.color}15, ${product.color}05)`,
                border: `1px solid ${product.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 40px 80px ${product.color}15`,
              }}>
                {product.svg}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        </SlideIn>

        {/* Right — content */}
        <SlideIn direction="right">
        <div>
          {/* Product tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 40 }}>
            {PRODUCTS.map((p, i) => (
              <button key={i} onClick={() => setActive(i)} style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${active === i ? p.color : 'var(--border)'}`,
                background: active === i ? p.color : 'white',
                color: active === i ? 'white' : 'var(--ink-600)',
                cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif',
              }}>{p.name}</button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: product.color, marginBottom: 12 }}>Featured Product</div>
              <h3 style={{ fontSize: 42, fontWeight: 900, color: 'var(--ink-900)', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 16 , fontFamily: "'Playfair Display', Georgia, serif" }}>{product.name}</h3>
              <p style={{ fontSize: 16, color: 'var(--ink-500)', lineHeight: 1.8, marginBottom: 32, maxWidth: 400 }}>{product.desc}</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to={`/products/${product.slug}`} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 12,
                  background: product.color, color: 'white',
                  textDecoration: 'none', fontWeight: 700, fontSize: 14,
                  boxShadow: `0 4px 16px ${product.color}33`,
                }}>
                  Learn more
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/builder" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 12,
                  border: '1.5px solid var(--border)', background: 'white',
                  color: 'var(--ink-700)', textDecoration: 'none', fontWeight: 700, fontSize: 14,
                }}>Design with AI</Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        </SlideIn>
      </div>
    </div>
  );
};

// Pin styles section
const PinStylesSection = () => {
  const [active, setActive] = useState(0);
  const style = PIN_STYLES[active];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 40, alignItems: 'center' }}>
        {/* Left sticky visual */}
        <SlideIn direction="left">
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: 560, aspectRatio: '1 / 1', margin: '0 auto' }}>
          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <img loading="lazy" decoding="async" src={style.image} alt={`${style.name} custom pin`} style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%', display: 'block', filter: 'drop-shadow(0 8px 16px rgba(10,15,30,0.16))' }} />
            </motion.div>
          </AnimatePresence>
          {/* Style number */}
          <div style={{ position: 'absolute', top: 0, right: 0, fontSize: 96, fontWeight: 900, color: `${BRAND_BLUE}08`, letterSpacing: '-0.05em', lineHeight: 1, pointerEvents: 'none' }}>
            0{active + 1}
          </div>
        </div>
        </SlideIn>

        {/* Right — style list */}
        <SlideIn direction="right">
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND_BLUE, marginBottom: 12 }}>Production Styles</div>
          <h3 style={{ fontSize: 36, fontWeight: 900, color: 'var(--ink-900)', letterSpacing: '-0.02em', marginBottom: 40 , fontFamily: "'Playfair Display', Georgia, serif" }}>Choose your finish</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {PIN_STYLES.map((s, i) => (
              <button key={i} onClick={() => setActive(i)} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
                borderRadius: 12, border: 'none', textAlign: 'left', cursor: 'pointer',
                background: active === i ? `${BRAND_BLUE}08` : 'transparent',
                borderLeft: `3px solid ${active === i ? BRAND_BLUE : 'transparent'}`,
                transition: 'all 0.2s', fontFamily: 'Inter, sans-serif',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: active === i ? BRAND_BLUE : 'var(--ink-700)', marginBottom: active === i ? 6 : 0, transition: 'all 0.2s' }}>{s.name}</div>
                  <AnimatePresence>
                    {active === i && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
                        <div style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.7 }}>{s.desc}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={active === i ? BRAND_BLUE : 'var(--ink-300)'} strokeWidth="2" style={{ flexShrink: 0, transform: active === i ? 'translateX(4px)' : 'none', transition: 'all 0.2s' }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 32 }}>
            <Link to="/how-it-works" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 24px', borderRadius: 12, background: BRAND_BLUE, minHeight: 48, color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
              See all styles
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
          </div>
        </div>
        </SlideIn>
      </div>
    </div>
  );
};

const STAGE_BG = ['#EBF2FF', '#FFF4E6', '#E8F7F0'];
const STAGE_NUM_COLOR = ['#1B3A8C', '#B45309', '#065F46'];

const StageCard = ({ stage, i, inView, isMobile }) => {
  const svgSrc = `/images/stage${i + 1}_blue.svg`;
  return (
    <motion.div
      initial={{ opacity: 0, x: -80 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay: i * 0.25, ease: [0.16, 1, 0.3, 1] }}
      style={{ flex: 1, borderRadius: 28, display: 'flex', flexDirection: 'column', background: STAGE_BG[i], position: 'relative', overflow: 'visible' }}
      whileHover={{ y: -5, boxShadow: '0 16px 40px rgba(0,0,0,0.10)' }}>

      {/* Circle — same color as card, no individual shadow, attached top-right */}
      <div style={{
        position: 'absolute', top: isMobile ? -28 : -44, right: isMobile ? -10 : -44,
        width: isMobile ? 130 : 200, height: isMobile ? 130 : 200, borderRadius: '50%',
        background: STAGE_BG[i],
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1,
      }}>
        <img loading="lazy" decoding="async" src={svgSrc} alt={stage.title} style={{ width: isMobile ? 80 : 130, height: isMobile ? 80 : 130, objectFit: 'contain' }} />
      </div>

      {/* Top — number + title + desc */}
      <div style={{ padding: isMobile ? '24px 110px 20px 24px' : '32px 165px 24px 32px', position: 'relative', zIndex: 2 }}>
        <div style={{ fontSize: 56, fontWeight: 900, color: STAGE_NUM_COLOR[i], letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 10, fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>{stage.stage}</div>
        <div style={{ width: 32, height: 3, borderRadius: 2, background: STAGE_NUM_COLOR[i], marginBottom: 12, opacity: 0.5 }} />
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0A0F1E', letterSpacing: '0.01em', marginBottom: 4, fontFamily: "'Playfair Display', Georgia, serif" }}>{stage.title}</div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8892A4' }}>{stage.desc}</div>
      </div>

      {/* Benefits */}
      <div style={{ padding: '20px 28px 32px', flex: 1, position: 'relative', zIndex: 2 }}>
        {stage.benefits.map((b, j) => (
          <motion.div key={j}
            initial={{ opacity: 0, x: -12 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.4, delay: i * 0.25 + j * 0.07 + 0.35 }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: j < stage.benefits.length - 1 ? 16 : 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={STAGE_NUM_COLOR[i]} strokeWidth="2.2" strokeLinecap="round"><path d={b.icon}/></svg>
            </div>
            <span style={{ fontSize: 14, color: '#1E2640', lineHeight: 1.65, paddingTop: 7 }}>{b.text}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const HWWConnector = ({ isMobile }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '4px 0' : '0 4px', flexShrink: 0, alignSelf: 'center', position: 'relative' }}>
    {isMobile ? (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <div style={{ width: 1, height: 16, background: 'linear-gradient(to bottom, #CBD5E1, #94A3B8)' }} />
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'white', border: '1.5px solid #E2E6F0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1B3A8C" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </div>
        <div style={{ width: 1, height: 16, background: 'linear-gradient(to bottom, #94A3B8, #CBD5E1)' }} />
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <div style={{ width: 20, height: 1, background: 'linear-gradient(to right, #CBD5E1, #94A3B8)' }} />
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'white', border: '1.5px solid #E2E6F0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1B3A8C" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </div>
        <div style={{ width: 20, height: 1, background: 'linear-gradient(to right, #94A3B8, #CBD5E1)' }} />
      </div>
    )}
  </div>
);

const STAGE_DATA = [
  { stage: '01', title: 'Artwork Proof', desc: 'Bring your idea to life', benefits: [
    { icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75', text: 'Expert artwork by our team within 120 minutes' },
    { icon: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', text: 'AI design builder — generate concepts instantly, free' },
    { icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z', text: 'Unlimited revisions until you love it' },
  ]},
  { stage: '02', title: 'Production', desc: 'Crafted with care', benefits: [
    { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', text: 'Premium quality — every order inspected before shipping' },
    { icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', text: 'Zero tooling or mold charges — ever' },
    { icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', text: 'Low minimums — small quantities welcome' },
    { icon: 'M1 4h22M1 10h22M5 4v16M19 4v16', text: '50% deposit, balance on approval' },
  ]},
  { stage: '03', title: 'Delivery', desc: 'Straight to your door', benefits: [
    { icon: 'M1 3h15v13H1zM16 8h4l3 5v3h-7V8zM5.5 21a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM18.5 21a1.5 1.5 0 100-3 1.5 1.5 0 000 3z', text: 'Free shipping on all orders across the USA' },
    { icon: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0zM12 10a2 2 0 100-4 2 2 0 000 4z', text: 'Real-time tracking from production to your door' },
  ]},
];

const StageCardsRow = () => {
  const rowRef = useRef(null);
  const rowInView = useInView(rowRef, { once: true, margin: '-60px' });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Three cards side by side need about 1100px; tablets get them stacked
  const [stacked, setStacked] = useState(window.innerWidth <= 1100);
  useEffect(() => {
    const h = () => { setIsMobile(window.innerWidth <= 768); setStacked(window.innerWidth <= 1100); };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return (
    <div ref={rowRef} style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', gap: 0, alignItems: 'stretch', paddingTop: 40, ...(stacked && !isMobile ? { maxWidth: 640, margin: '0 auto' } : {}) }}>
      {STAGE_DATA.map((stage, i) => (
        <React.Fragment key={i}>
          <StageCard stage={stage} i={i} inView={rowInView} isMobile={isMobile} />
          {i < 2 && <HWWConnector isMobile={stacked} />}
        </React.Fragment>
      ))}
    </div>
  );
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Below 1200px the four trust items no longer fit in one row, so they form a 2 x 2 grid
  const [isCompact, setIsCompact] = useState(window.innerWidth <= 1200);
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/designs/testimonials`)
      .then(r => r.json())
      .then(d => { if (d.testimonials) setTestimonials(d.testimonials); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handler = () => { setIsMobile(window.innerWidth <= 768); setIsCompact(window.innerWidth <= 1200); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div style={{ background: 'var(--surface)', overflowX: 'hidden' }}>
      <Helmet>
        <title>Brandster Pins — Custom Lapel Pins, Medals & Coins with AI Design</title>
        <meta name="description" content="Design custom lapel pins, trading pins, medals, challenge coins and PVC patches with AI. Artwork proof in 2 hours, free US shipping, no mold charges." />
        <link rel="canonical" href="https://brandsterpins.com/" />
      </Helmet>

      {/* Hero */}
      <div ref={heroRef} style={{ position: 'relative', height: isMobile ? 'calc(100vh - 60px)' : '100vh', paddingBottom: isMobile ? 24 : 0, paddingTop: isMobile ? 60 : 100, display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden', background: '#0A1030', zIndex: 1 }}>
        {/* Manufacturing video background */}
        {!isMobile && (
          <HeroVideo />
        )}
        {/* Brand Blue overlay — gradient: stronger on left (text), lighter on right */}
        {!isMobile && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: `linear-gradient(to right, rgba(13,30,74,0.82) 0%, rgba(27,58,140,0.65) 50%, rgba(27,58,140,0.45) 100%)` }} />
        )}
        {/* Mobile bg — product image with overlay */}
        {isMobile && (
          <>
            <img fetchPriority="high" decoding="async" src="/images/lapelPin_builder.webp" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center right', zIndex: 0, opacity: 0.45 }} />
            <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(160deg, rgba(8,16,60,0.82) 0%, rgba(15,30,100,0.72) 60%, rgba(10,20,70,0.78) 100%)' }} />
          </>
        )}

        <motion.div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '0 16px' : '0 40px', width: '100%', y: isMobile ? 0 : heroY, opacity: isMobile ? 1 : heroOpacity, position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 80, alignItems: 'center', paddingTop: isMobile ? 48 : 0, paddingBottom: isMobile ? 32 : 220 }}>
            {/* Left */}
            <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05, ease: EASE_OUT }} style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
              <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05, ease: [0.16,1,0.3,1] }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: isMobile ? '3px 8px' : '4px 10px', fontSize: isMobile ? 9 : 10, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: isMobile ? 12 : 18 }}>
                  <div style={{ width: isMobile ? 4 : 5, height: isMobile ? 4 : 5, borderRadius: '50%', background: '#4ADE80', animation: 'pulse 2s infinite' }} />
                  AI-Powered Design
                </div>
              </motion.div>
              <motion.h1 initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.13, ease: EASE_OUT }}
                style={{ fontSize: isMobile ? 42 : 64, fontWeight: 900, color: 'white', letterSpacing: '-0.04em', lineHeight: 1.0, marginBottom: 20, fontFamily: "'Playfair Display', Georgia, serif" }}>
                Your Idea.<br /><span style={{ color: '#93B8FF' }}>Made Real.</span>
              </motion.h1>
              <motion.p initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.21, ease: EASE_OUT }}
                style={{ fontSize: 18, color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, marginBottom: 40, maxWidth: 460 }}>
                From the first sketch to the finished product, we make custom pins, medals, coins and more — exactly the way you imagined them.
              </motion.p>
              <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.29, ease: EASE_OUT }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', gap: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  <Link to="/builder" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: isMobile ? '16px 36px' : '16px 32px', borderRadius: 14, minHeight: 52,
                    background: BRAND_RED, color: 'white',
                    textDecoration: 'none', fontWeight: 700, fontSize: 16,
                    boxShadow: '0 8px 32px rgba(212,31,58,0.4)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,31,58,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(212,31,58,0.4)'; }}>
                    Start Designing
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </Link>
                  {!isMobile && (
                    <Link to="/custom-order" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 10,
                      padding: '16px 32px', borderRadius: 14, minHeight: 52,
                      background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.3)',
                      color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: 16,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}>
                      Custom Request
                    </Link>
                  )}
                </div>
                {isMobile && (
                  <Link to="/custom-order" style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontWeight: 500, fontFamily: 'Inter, sans-serif', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: 1 }}>
                    or submit a Custom Request →
                  </Link>
                )}
              </motion.div>


            </motion.div>

            {/* Right — hero product */}
            {!isMobile && <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE_OUT }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {/* Main pin */}
              <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'relative', zIndex: 2 }}>
                <img fetchPriority="high" decoding="async" src="/images/homePage_heroSection_medal.webp" alt="Custom Medal" style={{ display: 'block', transform: 'scale(0.8)', filter: 'drop-shadow(0 34px 28px rgba(0,0,0,0.5)) drop-shadow(0 12px 10px rgba(0,0,0,0.35)) drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
              </motion.div>
              {/* Floating medal */}
              {!isMobile && <motion.div animate={{ y: [0, -8, 0], rotate: [0, 5, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                style={{ position: 'absolute', top: '-5%', right: '-10%' }}>
                <img src="/images/homePage_heroSection_pvc.webp" alt="PVC Patch" style={{ display: 'block', filter: 'drop-shadow(0 20px 18px rgba(0,0,0,0.5)) drop-shadow(0 8px 8px rgba(0,0,0,0.35)) drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
              </motion.div>}
              {/* Floating coin */}
              {!isMobile && <motion.div animate={{ y: [0, -10, 0], rotate: [0, -5, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                style={{ position: 'absolute', bottom: '2%', left: '-10%' }}>
                <img src="/images/homePage_heroSection_pin.webp" alt="Custom Pin" style={{ display: 'block', filter: 'drop-shadow(0 20px 18px rgba(0,0,0,0.5)) drop-shadow(0 8px 8px rgba(0,0,0,0.35)) drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
              </motion.div>}
              {/* Floating keychain */}
              {!isMobile && <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
                style={{ position: 'absolute', top: '72%', right: '-12%' }}>
                <img src="/images/homePage_heroSection_tradingPin.webp" alt="Trading Pin" style={{ display: 'block', filter: 'drop-shadow(0 20px 18px rgba(0,0,0,0.5)) drop-shadow(0 8px 8px rgba(0,0,0,0.35)) drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
              </motion.div>}
            </motion.div>}
          </div>
        </motion.div>

        {/* Mobile trust strip — 2x2 pill grid */}
        {isMobile && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease: EASE_OUT }} style={{ position: 'relative', zIndex: 3, width: '100%', alignSelf: 'stretch', marginTop: 'auto', padding: '0 16px 24px', boxSizing: 'border-box' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'calc(50% - 4px) calc(50% - 4px)', gap: 8 }}>
              {[
                { svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>, label: 'Designed in USA' },
                { svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 4v4h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>, label: 'Free Shipping' },
                { svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, label: 'Proof in 2hrs', accent: true },
                { svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>, label: 'No Mold Fees' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '9px 10px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  overflow: 'hidden',
                }}>
                  <div style={{ flexShrink: 0 }}>{item.svg}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: item.accent ? '#60A5FA' : 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

      {/* Trust panel — desktop only, absolute positioned */}
        {!isMobile && (
          <div style={{ position: 'absolute', bottom: isCompact ? 36 : 100, left: 0, right: 0, zIndex: 3, padding: isCompact ? '0 5%' : '0 12%' }}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}>
              <div style={{
                padding: '1.5px', borderRadius: 19,
                background: 'linear-gradient(135deg, rgba(180,220,255,0.7) 0%, rgba(100,160,255,0.25) 40%, rgba(60,100,200,0.12) 70%, rgba(180,220,255,0.45) 100%)',
                boxShadow: '0 0 20px rgba(96,165,250,0.22), 0 0 40px rgba(59,130,246,0.10), 0 8px 24px rgba(0,0,0,0.4)',
              }}>
                <div style={{
                  background: 'rgba(8,14,45,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                  borderRadius: 18, display: 'grid', gridTemplateColumns: isCompact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', overflow: 'hidden',
                  boxShadow: 'inset 0 0 60px rgba(96,165,250,0.07), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}>
                  {[
                    { svg: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>, label1: 'DESIGNED IN', label2: 'THE USA', accent: false, sub: 'Artwork by our US-based team.' },
                    { svg: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 4v4h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>, label1: 'FREE SHIPPING', label2: 'ON ALL ORDERS', accent: false, sub: 'No minimums. No extra costs.' },
                    { svg: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, label1: 'ARTWORK PROOF', label2: 'IN 2 HOURS', accent: true, sub: 'From idea to approval, fast.' },
                    { svg: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>, label1: 'NO MOLD', label2: 'CHARGES', accent: false, sub: 'More value from your first order.' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: isCompact ? 14 : 18, padding: isCompact ? '18px 20px' : '30px 24px', borderRight: (isCompact ? i % 2 === 0 : i < 3) ? '0.75px solid rgba(255,255,255,0.08)' : 'none', borderBottom: isCompact && i < 2 ? '0.75px solid rgba(255,255,255,0.08)' : 'none' }}>
                      <div style={{ flexShrink: 0, opacity: 0.85 }}>{item.svg}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: 'white', letterSpacing: '0.04em', lineHeight: 1.3 }}>{item.label1}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', lineHeight: 1.3, marginBottom: 5, color: item.accent ? '#60A5FA' : 'white' }}>{item.label2}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 400, lineHeight: 1.4 }}>{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Process & Benefits */}
      <div style={{ background: '#F5F7FA', position: 'relative', zIndex: 2, padding: 'clamp(48px,8vw,80px) 0' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 }}>
                <div style={{ height: 1, width: 48, background: '#C8CDE0' }} />
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8892A4' }}>How We Work</div>
                <div style={{ height: 1, width: 48, background: '#C8CDE0' }} />
              </div>
              <h2 style={{ fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, color: '#0A0F1E', letterSpacing: '0.01em', marginBottom: 16, fontFamily: "'Playfair Display', Georgia, serif" }}>
                From concept to <span style={{ color: '#1B3A8C' }}>doorstep</span>
              </h2>
              <p style={{ fontSize: 16, color: '#8892A4', maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>A seamless process to bring your custom products to life with speed, quality and zero hassle.</p>
            </div>
          </FadeUp>

          {/* Cards row */}
          <StageCardsRow />

          {/* Link to pin styles guide */}
          <FadeUp delay={0.4}>
            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center' }}>
              <Link to="/how-it-works" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 22px', borderRadius: 999, background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(27,58,140,0.15)', color: BRAND_BLUE, textDecoration: 'none', fontSize: 14, fontWeight: 700, boxShadow: '0 6px 20px rgba(27,58,140,0.08)' }}>
                See exactly what happens at every step, from idea to doorstep
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </FadeUp>

          {/* Journey indicator */}
          <FadeUp delay={0.5}>
            <div style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid var(--border-light)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8892A4' }}>Your Idea</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B3A8C" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1B3A8C' }}>Our Expertise</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B3A8C" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8892A4' }}>At Your Doorstep</span>
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Products section */}
      <div style={{ padding: '80px 0', background: 'var(--surface-blue-subtle)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND_BLUE, marginBottom: 12 }}>Our Products</div>
              <h2 style={{ fontSize: 42, fontWeight: 700, color: 'var(--ink-900)', letterSpacing: '-0.02em', marginBottom: 12, fontFamily: "'Playfair Display', Georgia, serif" }}>Custom promotional products</h2>
              <p style={{ fontSize: 15, color: 'var(--ink-400)', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>From lapel pins to challenge coins — manufactured to order for brands, teams and events across the US.</p>
            </div>
          </FadeUp>
          <StaggerContainer delay={0.1}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: isMobile ? 12 : 20 }}>
            {[
              { label: 'Lapel Pins', desc: 'Soft enamel, hard enamel, die struck & more', image: '/images/lapelpinPage_heroSection_lapelPin.webp', fit: '87%', to: '/products/lapel-pins', color: '#1B3A8C' },
              { label: 'Trading Pins', desc: 'Baseball, softball & sports collectibles', image: '/images/tradingPinsPage_heroSection_tradingPin.webp', fit: '87%', to: '/products/trading-pins', color: '#DC2626' },
              { label: 'Medals', desc: 'Sports, race, award & recognition medals', image: '/images/medalsPage_heroSection_medal.webp', fit: '87%', to: '/products/medals', color: '#D97706' },
              { label: 'Challenge Coins', desc: 'Military, police, corporate & commemorative', image: '/images/coinsPage_heroSection_coin.webp', fit: '87%', to: '/products/coins', color: '#7C3AED' },
              { label: 'PVC Patches', desc: 'Military, morale & tactical rubber patches', image: '/images/pvcPatchesPage_heroSection_pvcPatch.webp', fit: '87%', to: '/products/pvc-patches', color: '#16A34A' },
              { label: 'Keychains', desc: 'Promotional & souvenir keychains', image: '/images/homePage_products_keyChain.webp', to: '/products/keychains', color: '#0D9488' },
              { label: 'Charms', desc: 'Jewelry, zipper & bag charms', image: '/images/homePage_products_charm.webp', to: '/products/charms', color: '#E11D48' },
              { label: 'Belt Buckles', desc: 'Western, award & championship buckles', image: '/images/homePage_products_beltBuckle.webp', to: '/products/belt-buckles', color: '#CA8A04' },
            ].map((p, i) => (
              <StaggerChild key={i}>
                <Link to={p.to} style={{ textDecoration: 'none', display: 'block' }}>
                  <div style={{ borderRadius: isMobile ? 16 : 20, overflow: 'hidden', position: 'relative', aspectRatio: '3/4', background: 'var(--surface)', border: isMobile ? 'none' : '1px solid var(--border-light)', transition: 'all 0.3s', cursor: 'pointer' }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,58,140,0.10)';
                      const img = e.currentTarget.querySelector('img');
                      if (img) { img.style.transform = 'scale(1.02)'; }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                      const img = e.currentTarget.querySelector('img');
                      if (img) { img.style.transform = 'none'; }
                    }}>

                    {/* Product image — fills card */}
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img loading="lazy" decoding="async" src={p.image} alt={p.label} style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: p.fit || '100%', maxHeight: p.fit || '100%', transition: 'transform 0.3s ease' }} />
                    </div>

                    {/* Reduced gradient — just enough for text legibility */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '42%', background: 'linear-gradient(to top, rgba(255,255,255,0.98) 40%, rgba(255,255,255,0) 100%)' }} />

                    {/* Product info at bottom */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: isMobile ? '8px 10px' : '14px 18px' }}>
                      <div style={{ fontSize: isMobile ? 12 : 16, fontWeight: 700, color: 'var(--ink-900)', letterSpacing: '-0.01em', marginBottom: isMobile ? 4 : 8, fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>{p.label}</div>
                      {!isMobile && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 999, background: 'var(--brand-blue)', color: 'white', fontSize: 12, fontWeight: 600 }}>
                        Explore {p.label}
                        <span className="cta-arrow" style={{ display: 'inline-block', transition: 'transform 0.25s ease' }}>→</span>
                      </div>}
                      {isMobile && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 999, background: 'var(--brand-blue)', color: 'white', fontSize: 9, fontWeight: 600 }}>
                        Explore
                        <span style={{ display: 'inline-block' }}>→</span>
                      </div>}
                    </div>
                  </div>
                </Link>
              </StaggerChild>
            ))}
          </div>
          </StaggerContainer>
        </div>
      </div>

      {/* Testimonials */}
      {SHOW_TESTIMONIALS && (
      <div style={{ background: 'var(--surface-blue-subtle)', padding: 'clamp(48px,8vw,100px) 0' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: BRAND_BLUE, marginBottom: 12 }}>Customer Stories</div>
              <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 700, color: 'var(--ink-900)', letterSpacing: '0.01em', fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 12 }}>
                Loved by teams across America
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ink-400)', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
                Real products, real people — from little league teams to Fortune 500 companies.
              </p>
            </div>
          </FadeUp>

          <div style={{ position: 'relative' }}>
            <div id="testimonialScroll" style={{ display: 'flex', gap: 20, overflowX: 'auto', paddingBottom: 16, scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {testimonials.length === 0
                ? Array.from({ length: 4 }).map((_, i) => <SkeletonTestimonial key={i} />)
                : testimonials.map((t, i) => (
                <div key={i} style={{ flexShrink: 0, width: 300, scrollSnapAlign: 'start' }}>
                  <div style={{ borderRadius: 20, overflow: 'hidden', background: 'white', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', transition: 'all 0.25s', height: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.13)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.07)'; }}>
                    <div style={{ position: 'relative', overflow: 'hidden' }}>
                      <img loading="lazy" decoding="async" src={t.image_url} alt={t.product} style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', top: 12, left: 12, padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.92)', fontSize: 10, fontWeight: 700, color: `color-mix(in srgb, ${t.color || BRAND_BLUE} 62%, #000)`, letterSpacing: '0.06em' }}>
                        {t.product}
                      </div>
                      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 2, background: 'rgba(255,255,255,0.92)', padding: '4px 8px', borderRadius: 999 }}>
                        {[...Array(5)].map((_, s) => (
                          <svg key={s} width="11" height="11" viewBox="0 0 24 24" fill="#F59E0B" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        ))}
                      </div>
                    </div>
                    <div style={{ padding: '16px 18px 18px' }}>
                      <svg aria-hidden="true" width="26" height="26" viewBox="0 0 24 24" fill={`${t.color || BRAND_BLUE}30`} style={{ display: 'block', marginBottom: 8 }}><path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/><path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/></svg>
                      <p style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.65, marginBottom: 14 }}>{t.quote}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${t.color || BRAND_BLUE}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: `color-mix(in srgb, ${t.color || BRAND_BLUE} 62%, #000)` }}>{t.initials}</span>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)' }}>{t.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{t.role} · {t.location}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { const el = document.getElementById('testimonialScroll'); el.scrollBy({ left: -320, behavior: 'smooth' }); }}
              aria-label="Previous testimonials"
              style={{ position: 'absolute', left: -16, top: '40%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', background: 'white', border: '1.5px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button onClick={() => { const el = document.getElementById('testimonialScroll'); el.scrollBy({ left: 320, behavior: 'smooth' }); }}
              aria-label="Next testimonials"
              style={{ position: 'absolute', right: -16, top: '40%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', background: 'white', border: '1.5px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Pin styles section */}
      <div style={{ background: 'white', padding: '100px 0' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--brand-blue)', marginBottom: 12 }}>Custom Pins</div>
            <h2 style={{ fontSize: 48, fontWeight: 900, color: 'var(--ink-900)', letterSpacing: '-0.03em', fontFamily: "'Playfair Display', Georgia, serif" }}>Five ways to make a pin.</h2>
          </div>
        </FadeUp>
        <PinStylesSection />
      </div>

      {/* Footer */}
      <footer style={{ background: 'linear-gradient(135deg, #0A0F1E 0%, #1B3A8C 100%)' }}>

        {/* CTA top band */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', padding: 'clamp(48px,6vw,80px) clamp(16px,4vw,40px)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 }}>
            <div>
              <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: 700, color: 'white', letterSpacing: '0.01em', lineHeight: 1.2, marginBottom: 10, fontFamily: "'Playfair Display', Georgia, serif" }}>
                Ready to create something great?
              </h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', maxWidth: 480, lineHeight: 1.7 }}>
                AI designs in seconds. Expert artwork in 120 minutes. Free shipping across the USA.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/builder" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 12, background: BRAND_RED, color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: 15, boxShadow: '0 4px 20px rgba(212,31,58,0.4)' }}>
                Start Designing
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
              <Link to="/custom-order" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: 15 }}>
                Custom Request
              </Link>
            </div>
          </div>
        </div>

        {/* Main footer columns */}
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '56px clamp(16px,4vw,40px) 40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40 }}>

            {/* Col 1 — Brand */}
            <div>
              <img loading="lazy" decoding="async" src="/images/logo_white.svg" alt="BrandsterPins" style={{ width: 220, height: 'auto', marginBottom: 16 }} />
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.8, marginBottom: 8, maxWidth: 220 }}>
                Custom promotional products for brands, teams and events across the USA.
              </p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, marginBottom: 24, maxWidth: 220, fontStyle: 'italic' }}>
                Designed in the USA, expertly crafted and shipped worldwide.
              </p>
              {/* Social icons */}
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z', label: 'Instagram' },
                  { icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', label: 'Facebook' },
                  { icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.736-8.849L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z', label: 'X' },
                  { icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z', label: 'LinkedIn' },
                ].map(s => (
                  <a key={s.label} href="#" aria-label={s.label} style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="rgba(255,255,255,0.6)"><path d={s.icon}/></svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2 — Products */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 20 }}>Products</div>
              {[
                { to: '/products/lapel-pins', label: 'Lapel Pins' },
                { to: '/products/trading-pins', label: 'Trading Pins' },
                { to: '/products/medals', label: 'Medals' },
                { to: '/products/coins', label: 'Challenge Coins' },
                { to: '/products/pvc-patches', label: 'PVC Patches' },
                { to: '/products/keychains', label: 'Keychains' },
                { to: '/products/charms', label: 'Charms' },
                { to: '/products/belt-buckles', label: 'Belt Buckles' },
              ].map(link => (
                <Link key={link.to} to={link.to} style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Col 3 — Company */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 20 }}>Company</div>
              {[
                { to: '/builder', label: 'AI Builder' },
                { to: '/custom-order', label: 'Custom Request' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/faq', label: 'FAQ' },
                { to: '/products', label: 'All Products' },
              ].map(link => (
                <Link key={link.to} to={link.to} style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Col 4 — Contact */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 20 }}>Contact</div>
              <a href="mailto:support@brandsterpins.com" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 16, transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                support@brandsterpins.com
              </a>
              {/* Trust badges */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10', text: 'Free US shipping' },
                  { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', text: 'Quality guaranteed' },
                  { icon: 'M13 10V3L4 14h7v7l9-11h-7z', text: 'Proof in 120 minutes' },
                ].map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.8"><path d={b.icon}/></svg>
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '20px clamp(16px,4vw,40px)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>© 2026 Brandster Pins. All rights reserved.</div>
            <div style={{ display: 'flex', gap: 24 }}>
              {[{ to: '/privacy-policy', label: 'Privacy Policy' }, { to: '/terms-of-service', label: 'Terms of Service' }, { to: '/shipping-policy', label: 'Shipping & Returns' }].map(link => (
                <Link key={link.to} to={link.to} style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

      </footer>

    </div>
  );
};

export default Home;
